import React from 'react';

import { getBtnData, getStockByProductName } from '../api/Api';
import { cardClasses } from '@mui/material';
import { getFirestore, collection, getDoc, getDocs, doc } from 'firebase/firestore';
import { addDoc, updateDoc, setDoc } from '../api/FirestoreInterceptor';
import { db, auth } from '../config-firebase/firebase.js';
import { signInAnonymously } from 'firebase/auth';
import * as XLSX from 'xlsx';

export const isEmptyObject = (obj) => {
  if (!obj || obj.firstLabel === '' || obj.firstLabel === undefined) {
    return true;
  } else {
    return false;
  }
};

export async function checkUnavaiableRawMaterial(id) {
  // 1) Busca em lote de todos os produtos do estoque (1 única leitura rápida)
  const stockSnapshot = await getDocs(collection(db, 'stock'));
  const stockMap = new Map();

  const normalize = (str) =>
    (str || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  stockSnapshot.docs.forEach((docSnap) => {
    const s = docSnap.data();
    if (s && s.product) {
      const totalVolume = Number(s.totalVolume) || 0;
      const disabledDish = Number(s.disabledDish) || 0;
      const isUnavailable = disabledDish >= totalVolume;

      const stockInfo = {
        id: docSnap.id,
        idProduct: s.idProduct,
        product: s.product,
        totalVolume,
        disabledDish,
        isUnavailable,
      };

      stockMap.set(normalize(s.product), stockInfo);
      if (s.idProduct) {
        stockMap.set(s.idProduct, stockInfo);
      }
    }
  });

  // 2) Busca todos os pratos (collection 'item')
  const itemsSnapshot = await getDocs(collection(db, 'item'));

  // 3) Processa cada prato e seus ingredientes em memória
  for (const dishSnap of itemsSnapshot.docs) {
    const data = dishSnap.data();
    let recipeChanged = false;

    const recipe = data?.recipe?.FinalingridientsList;
    if (!recipe) continue;

    // Clone seguro
    let updatedRecipe = structuredClone(recipe);

    const processIngredientsArray = (arr) => {
      if (!Array.isArray(arr)) return;

      arr.forEach((ing) => {
        if (!ing || typeof ing.name !== 'string') return;
        const normalizedIngName = normalize(ing.name);

        // Busca a matéria-prima no mapa de estoque
        const matchedStock = stockMap.get(normalizedIngName) || (ing.idProduct ? stockMap.get(ing.idProduct) : null);

        if (matchedStock) {
          if (ing.unavailableRawMaterial !== matchedStock.isUnavailable) {
            ing.unavailableRawMaterial = matchedStock.isUnavailable;
            recipeChanged = true;
          }
        } else {
          // Se não existir matéria-prima no estoque, garante que não trava o prato
          if (ing.unavailableRawMaterial === true) {
            ing.unavailableRawMaterial = false;
            recipeChanged = true;
          }
        }
      });
    };

    // 4) Processa os ingredientes conforme estrutura da receita
    if (Array.isArray(updatedRecipe)) {
      processIngredientsArray(updatedRecipe);
    } else if (typeof updatedRecipe === 'object' && updatedRecipe !== null) {
      const labels = data.CustomizedPrice;

      // Chaves fixas
      processIngredientsArray(updatedRecipe.firstPrice);
      processIngredientsArray(updatedRecipe.secondPrice);
      processIngredientsArray(updatedRecipe.thirdPrice);

      // Chaves por labels customizados
      if (labels) {
        processIngredientsArray(updatedRecipe[labels.firstLabel]);
        processIngredientsArray(updatedRecipe[labels.secondLabel]);
        processIngredientsArray(updatedRecipe[labels.thirdLabel]);
      }
    }

    // 5) Ajuste da flag lowAmountRawMaterial no prato
    const originalLowAmount = !!data.lowAmountRawMaterial;

    const checkArrayUnavailable = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return false;
      return arr.some((ing) => ing?.unavailableRawMaterial === true);
    };

    let nextLowAmount = false;
    if (Array.isArray(updatedRecipe)) {
      nextLowAmount = checkArrayUnavailable(updatedRecipe);
    } else if (typeof updatedRecipe === 'object' && updatedRecipe !== null) {
      const labels = data.CustomizedPrice;
      const activeGroups = [];

      if (Array.isArray(updatedRecipe.firstPrice) && updatedRecipe.firstPrice.length > 0) activeGroups.push(updatedRecipe.firstPrice);
      if (Array.isArray(updatedRecipe.secondPrice) && updatedRecipe.secondPrice.length > 0) activeGroups.push(updatedRecipe.secondPrice);
      if (Array.isArray(updatedRecipe.thirdPrice) && updatedRecipe.thirdPrice.length > 0) activeGroups.push(updatedRecipe.thirdPrice);

      if (labels) {
        if (labels.firstLabel && Array.isArray(updatedRecipe[labels.firstLabel]) && updatedRecipe[labels.firstLabel].length > 0) {
          activeGroups.push(updatedRecipe[labels.firstLabel]);
        }
        if (labels.secondLabel && Array.isArray(updatedRecipe[labels.secondLabel]) && updatedRecipe[labels.secondLabel].length > 0) {
          activeGroups.push(updatedRecipe[labels.secondLabel]);
        }
        if (labels.thirdLabel && Array.isArray(updatedRecipe[labels.thirdLabel]) && updatedRecipe[labels.thirdLabel].length > 0) {
          activeGroups.push(updatedRecipe[labels.thirdLabel]);
        }
      }

      nextLowAmount = activeGroups.some((group) => checkArrayUnavailable(group));
    }

    if (originalLowAmount !== nextLowAmount) {
      data.lowAmountRawMaterial = nextLowAmount;
      recipeChanged = true;
    }

    // 6) Salva no Firestore somente se houver alteração real
    if (recipeChanged) {
      console.log(
        `[OPTIMIZATION] Prato ${data.title} atualizado no Firestore: lowAmountRawMaterial = ${data.lowAmountRawMaterial}`,
      );

      const dishRef = doc(db, 'item', dishSnap.id);
      let updatedPayload;

      if (Array.isArray(recipe)) {
        updatedPayload = {
          ...data,
          recipe: {
            FinalingridientsList: updatedRecipe,
          },
        };
      } else {
        updatedPayload = {
          ...data,
          recipe: {
            FinalingridientsList: updatedRecipe,
          },
          CustomizedPrice: data.CustomizedPrice,
        };
      }

      await updateDoc(dishRef, updatedPayload);
    }
  }

  return false;
}

// helpers/alertMinimumAmount.js

export const alertMinimunAmount = (product, volume, minimum, cost) => {
  // Caso especial: estoque zerado intencionalmente (sem estoque e sem volume mínimo)
  if (Number(volume) === 0 && Number(minimum) === 0) {
    return { status: true, message: '' };
  }

  if (volume < minimum) {
    return {
      status: false,
      message: `⚠️ O produto ${product} foi recusado porque o volume (${volume}) está menor que o mínimo (${minimum})`,
    };
  }

  if (volume === 0) {
    return {
      status: false,
      message: ` O produto ${product} foi recusado porque o volume está igual a 0`,
    };
  }

  if (minimum === undefined) {
    console.log(`⚠️`);
    return {
      status: false,
      message: ` O produto ${product} foi recusado porque o mínimo está indefinido`,
    };
  }

  if (isNaN(cost)) {
    return {
      status: false,
      message: `O produto ${product} foi recusado porque o custo não é um número`,
    };
  }

  if (cost === undefined) {
    return {
      status: false,
      message: `O produto ${product} foi recusado porque o custo está indefinido`,
    };
  }

  if (cost <= 0) {
    return {
      status: false,
      message: `O produto ${product} foi recusado porque o custo (${cost}) é menor ou igual a 0`,
    };
  }

  // ✅ Se passou em todas as verificações:
  return { status: true, message: '' };
};

export const exportToExcel = (ObjList, fileName = 'data.xlsx') => {
  if (!ObjList.length) {
    alert('Nenhum cliente para exportar!');
    return;
  }

  try {
    // Mantém apenas chaves com valores primitivos (string, number, boolean)
    const simplifiedList = ObjList.map((obj) => {
      const flatObj = {};
      for (const key in obj) {
        const value = obj[key];
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          flatObj[key] = value;
        }
      }
      return flatObj;
    });

    const worksheet = XLSX.utils.json_to_sheet(simplifiedList);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados');
    XLSX.writeFile(workbook, fileName);

    console.log('Arquivo Excel gerado com sucesso!');
    alert('Arquivo Excel foi baixado com sucesso!');
  } catch (err) {
    console.error('Erro ao exportar Excel:', err);
    alert('Erro ao gerar o arquivo Excel');
  }
};

export function firstNameClient(nameCustomer) {
  if (typeof nameCustomer === 'string' && nameCustomer.trim()) {
    const cleanName = nameCustomer.trim();
    const firstName = cleanName.split(/\s+/)[0];
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  }
  return '';
}

export function getFirstFourLetters(inputString, max) {
  return inputString ? inputString.slice(0, max) : '';
}

export async function CheckUser(check, isTotenParam, packageTier) {
  const isToten = isTotenParam || localStorage.getItem('isToten') === 'true';
  const isPdv = localStorage.getItem('pdv') === 'true';

  if (isToten || isPdv) {
    if (!localStorage.hasOwnProperty(check)) {
      const defaultNoCustomer = {
        name: 'anonimo',
        phone: '777',
        birthday: '77',
        email: 'anonimo@anonimo.com',
        request: [],
      };
      const currentUid = auth.currentUser?.uid || `legacy_anon_${Date.now()}`;
      const updatedUser = {
        id: currentUid,
        name: 'anonimo',
        migratedToAuth: false,
      };
      localStorage.setItem(check, JSON.stringify(updatedUser));
      try {
        const docRef = doc(db, 'user', currentUid);
        getDoc(docRef).then((snap) => {
          if (!snap.exists()) {
            setDoc(docRef, defaultNoCustomer).catch((err) =>
              console.warn('Erro ao criar doc anônimo no CheckUser:', err),
            );
          }
        }).catch(() => {});
      } catch (e) {}
    }
    return isPdv ? '/admin/requestlist' : '/new-layout';
  }

  if (localStorage.hasOwnProperty(check)) {
    const userMenu = JSON.parse(localStorage.getItem(check));
    if (!userMenu || !userMenu.id) {
      localStorage.removeItem(check);
      return Number(packageTier) === 1 ? '/create-customer' : '/admin/check-customer-nolog';
    }

    try {
      const userDocRef = doc(db, 'user', userMenu.id);
      const userSnap = await getDoc(userDocRef);

      if (userSnap.exists() && userSnap.data()?.name) {
        if (localStorage.getItem('pdv') === 'true') {
          return '/admin/requestlist';
        } else {
          return '/';
        }
      } else {
        localStorage.removeItem(check);
        return Number(packageTier) === 1 ? '/create-customer' : '/admin/check-customer-nolog';
      }
    } catch (error) {
      console.warn('Erro ao verificar usuário no Firestore (mantendo sessão local):', error);
      if (localStorage.getItem('pdv') === 'true') {
        return '/admin/requestlist';
      } else {
        return '/';
      }
    }
  } else {
    if (Number(packageTier) === 1) {
      return '/create-customer';
    }
    return '/admin/check-customer-nolog';
  }
}


export const requestSorter = (ObjList, direction) => {
  const sortedList = [...ObjList].map((item) => ({
    ...item,
    countRequest: Number(item.countRequest) || 0, // força número ou 0
  }));

  sortedList.sort((a, b) => {
    return direction
      ? b.countRequest - a.countRequest
      : a.countRequest - b.countRequest;
  });

  return sortedList;
};

export const isOrderFullyFinished = (item) => {
  if (!item.request || item.request.length === 0) return false;
  // Um pedido está totalmente finalizado se todos os pratos estão PRONTOS e ENTREGUES pela cozinha
  return item.request.every((req) => req.pronto && req.entregue);
};

export const logToAnounimousInToten = (setNameClient) => {
  const noCustomer = {
    name: 'anonimo',
    phone: '777',
    birthday: '77',
    email: 'anonimo@anonimo.com',
  };
  if (localStorage.hasOwnProperty('isToten')) {
    if (localStorage.hasOwnProperty('userMenu')) {
      const currentUserNew = JSON.parse(localStorage.getItem('userMenu'));
      if (currentUserNew) {
        setNameClient(currentUserNew.name);
        if (typeof global !== 'undefined' && global.setId) {
          global.setId(currentUserNew.name);
        }
      }
    } else {
      const executeTotenAuth = async () => {
        try {
          let currentUser = auth.currentUser;
          if (!currentUser) {
            const userCredential = await signInAnonymously(auth);
            currentUser = userCredential.user;
          }
          const uid = currentUser.uid;
          await setDoc(doc(db, 'user', uid), noCustomer);
          if (typeof global !== 'undefined' && global.setId) {
            global.setId(uid);
          }
          console.log('Toten document written with UID: ', uid);
          setNameClient('anonimo');
          localStorage.setItem(
            'userMenu',
            JSON.stringify({ id: uid, name: 'anonimo', migratedToAuth: true }),
          );
        } catch (e) {
          console.error("Erro no login anônimo Toten no Helpers:", e);
        }
      };
      executeTotenAuth();
    }
  }
};

export async function calculateItemCost(item) {
  const results = {};

  const processIngredientList = async (ingredientList) => {
    let totalCost = 0;
    const invalidIngredients = [];
    console.log('Ingredient List:', ingredientList);
    for (const ingredient of ingredientList) {
      const stock = await getStockByProductName(ingredient.name);
      if (stock && stock.totalVolume && stock.totalCost) {
        const costPerUnit = stock.totalCost / stock.totalVolume;

        const amount = parseFloat(ingredient.amount.replace(',', '.')); // garante conversão correta

        totalCost += amount * costPerUnit;
      } else {
        invalidIngredients.push(ingredient.name);
        console.warn(
          `Stock not found or invalid for ingredient: ${ingredient.name}`,
        );
        if (invalidIngredients.length > 0) {
          alert(
            `O valor do custo não está correto porque os seguintes ingredientes não têm valores válidos: ${invalidIngredients.join(
              ', ',
            )}. O cálculo foi feito apenas com os ingredientes válidos.`,
          );
        }
      }
    }

    // Arredonda para 2 casas decimais
    totalCost = Math.round((totalCost + Number.EPSILON) * 100) / 100;

    return Number(totalCost.toFixed(2));
  };

  // Caso 1: FinalingredientList é um array
  if (Array.isArray(item?.FinalingridientsList)) {
    const cost = await processIngredientList(item?.FinalingridientsList);
    return { default: cost }; // retorna com uma key padrão
  }

  // Caso 2: FinalingredientList é um objeto com arrays
  if (
    typeof item?.FinalingridientsList === 'object' &&
    item?.FinalingridientsList !== null
  ) {
    const sizes = item.FinalingridientsList;

    for (const [sizeName, ingredientList] of Object.entries(sizes)) {
      const cost = await processIngredientList(ingredientList);
      results[sizeName] = Number(cost.toFixed(2));
    }

    return results;
  }

  return null;
}
//There is no need to export this function, it is used only in the PriceAndExpenseBuilder.js
//te
